/**
 * utils/duplicateAudit.js
 *
 * Duplicate an existing audit into a fresh audit doc with a new UUID.
 *
 * Data model recap:
 *   auditReports/{auditId}                ← parent doc (name, tenant, status)
 *     form_a/main                          ← Form A data
 *     form_b/main                          ← Form B data
 *     form_c/main                          ← Form C data
 *     form_d/main                          ← Form D data
 *     form_e/main                          ← Form E data
 *     form_f/main                          ← Form F data
 *
 * Rules (senior-approved, Jul 2026 revision):
 *   1. Duplicate flow presents three options:
 *        'installation_only' — copy Form A only
 *        'complete'          — copy Form A + B + C + D + E + F
 *        'blank'             — no copy at all (handled outside this util —
 *                              the dialog navigates to /form-a/new instead
 *                              of calling duplicateAudit).
 *   2. Reporting period always resets on the new audit's Form A.
 *   3. Missing source subcollection docs are silently skipped.
 *   4. Atomic writes via Firestore writeBatch.
 *   5. Name is auto-prefixed with "Copy_ " (users rename after).
 *   6. Parent doc gets fresh UUID, timestamps, current user, status='draft',
 *      and `duplicatedFrom: sourceAuditId` for provenance.
 */

import {
  doc, getDoc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { firestore as db } from '../components/firebase.config';

// ─── Duplication modes ──────────────────────────────────────────────────────
export const DUPLICATE_MODES = {
  INSTALLATION_ONLY: 'installation_only',   // Form A only
  COMPLETE: 'complete',                     // A + B + C + D + E + F
  DEFAULTS_ONLY: 'defaults_only',           // A + B + C + D + E + F, values cleared
};

// ─── Helpers ────────────────────────────────────────────────────────────────
/**
 * Build the duplicate name. Always prefixes "Copy_ " even if the source
 * name already starts with "Copy_ " (per senior — double prefix is fine,
 * user can rename after).
 */
export const buildDuplicateName = (sourceName) => {
  const base = (sourceName || 'Untitled').trim();
  return `Copy_ ${base}`;
};

// Form keys copied for each mode
const FORMS_FOR_MODE = {
  [DUPLICATE_MODES.INSTALLATION_ONLY]: ['a'],
  [DUPLICATE_MODES.COMPLETE]: ['a', 'b', 'c', 'd', 'e', 'f'],
  [DUPLICATE_MODES.DEFAULTS_ONLY]: ['a', 'b', 'c', 'd', 'e', 'f'],
};

const createBlankStringRow = (template, fields) => {
  const result = { ...template };
  fields.forEach((field) => {
    result[field] = '';
  });
  return result;
};

const sanitizeFormAForDefaults = (data = {}) => {
  const blankGoodsRow = (row = {}, index = 0) => ({
    id: row.id || `G${index + 1}`,
    category: '',
    cnCode: '',
    primaryRoute: '',
    route1: '',
    route2: '',
    route3: '',
    route4: '',
    route5: '',
    route6: '',
  });

  const blankProcessRow = (row = {}, index = 0) => ({
    id: row.id || `P${index + 1}`,
    aggregatedGoodsCategory: row.aggregatedGoodsCategory || '',
    inc1: '',
    inc2: '',
    inc3: '',
    inc4: '',
    inc5: '',
    inc6: '',
    name: '',
    errorMessage: '',
  });

  const blankPrecursorRow = (row = {}, index = 0) => ({
    id: row.id || `PP${index + 1}`,
    goodsCategory: row.goodsCategory || '',
    linkedProcessId: row.linkedProcessId || '',
    countryCode: '',
    cnCode: row.cnCode || '',
    route1: '',
    route2: '',
    route3: '',
    route4: '',
    route5: '',
    name: '',
    error: '',
  });

  return {
    reportingPeriod: { start: null, end: null },
    installation: {
      organisationType: '',
      name: '',
      sector: '',
      street: '',
      economicActivity: '',
      postCode: '',
      poBox: '',
      city: '',
      country: '',
      unLocode: '',
      latitude: '',
      longitude: '',
    },
    verifier: {
      companyName: '',
      street: '',
      city: '',
      postcode: '',
      country: '',
      representativeName: '',
      email: '',
      telephone: '',
      fax: '',
      accreditationMemberState: '',
      accreditationNationalAuthority: '',
      registrationNumber: '',
    },
    aggregatedGoods: [blankGoodsRow({}, 0)],
    relevantProcesses: [blankProcessRow({}, 0)],
    purchasedPrecursors: [blankPrecursorRow({}, 0)],
  };
};

const sanitizeFormEForDefaults = (formAData = {}, formEData = {}) => {
  const sourcePrecursors = Array.isArray(formAData.purchasedPrecursors) && formAData.purchasedPrecursors.length > 0
    ? formAData.purchasedPrecursors
    : Array.isArray(formEData.precursors)
      ? formEData.precursors
      : [];

  const blankPrecursor = (source = {}, index = 0) => ({
    id: source.id || `PE${index + 1}`,
    formAPrecursorId: source.id || '',
    title: '',
    countryCode: '',
    goodsCategory: source.goodsCategory || '',
    linkedProcessId: source.linkedProcessId || '',
    originRoutes: [],
    totalPurchasedLevels: Array.from({ length: 5 }).map(() => ({
      totalPurchasedLevel: '',
      productionRoute: '',
      unit: '',
      amounts: '',
    })),
    totalPurchaseConsumption: '',
    consumedInProcesses: Array.from({ length: 10 }).map(() => ({
      processId: '',
      process: '',
      unit: '',
      amounts: '',
    })),
    consumedOtherPurposes: [
      { unit: '', amounts: '' },
      { unit: '', amounts: '' },
    ],
    control: [
      { unit: '', amounts: '' },
      { unit: '', amounts: '' },
    ],
    emissions: {
      seeDirect: '',
      sourceSeeDirect: source.cnCode ? 'Default' : '',
      secIndirect: '',
      secIndirectSource: source.cnCode ? 'Default' : '',
      eefIndirect: '',
      eefIndirectSource: source.cnCode ? 'Default' : '',
      seeIndirect: '',
      totalDirectEmissions: '',
      totalIndirectEmissions: '',
      justification: '',
      justificationOther: '',
      cnCode: source.cnCode || '',
    },
    _materialBalanceCheck: { balance: 0, isValid: true, message: '' },
    _dataQualityComplete: false,
  });

  return {
    precursors: sourcePrecursors.map(blankPrecursor),
  };
};

const sanitizeBlankForm = () => ({ updatedAt: new Date() });

/**
 * Duplicate an audit.
 *
 * @param {Object} params
 * @param {string} params.sourceAuditId
 * @param {string} params.mode          one of DUPLICATE_MODES.*
 * @param {Object} params.user          { email, tenantId, tenantName }
 * @returns {Promise<{ newAuditId: string, newName: string }>}
 */
export async function duplicateAudit({ sourceAuditId, mode, user }) {
  if (!sourceAuditId) throw new Error('sourceAuditId is required');
  if (!user?.email) throw new Error('user.email is required');
  if (!user?.tenantId) throw new Error('user.tenantId is required');

  const formsToCopy = FORMS_FOR_MODE[mode];
  if (!formsToCopy) throw new Error(`Unknown duplicate mode: ${mode}`);

  // 1. Read source parent doc
  const srcParentRef = doc(db, 'auditReports', sourceAuditId);
  const srcParentSnap = await getDoc(srcParentRef);
  if (!srcParentSnap.exists()) {
    throw new Error('Source audit not found');
  }
  const src = srcParentSnap.data();

  // 2. Read all source subcollection docs for the selected mode
  const sourceDataByForm = {};
  const subDocSnaps = await Promise.all(
    formsToCopy.map(async (k) => {
      const snap = await getDoc(doc(db, 'auditReports', sourceAuditId, `form_${k}`, 'main'));
      sourceDataByForm[k] = snap.exists() ? snap.data() : {};
      return snap;
    })
  );

  // 3. Prepare new IDs + names
  const newAuditId = crypto.randomUUID();
  const finalName = buildDuplicateName(src.name);

  // 4. Build a single atomic batch
  const batch = writeBatch(db);

  // 4a. Parent doc — fresh identity, timestamps, status, plus provenance
  batch.set(doc(db, 'auditReports', newAuditId), {
    name: finalName,
    description: mode === DUPLICATE_MODES.DEFAULTS_ONLY ? '' : src.description || '',
    reportingFrequency: mode === DUPLICATE_MODES.DEFAULTS_ONLY ? '' : src.reportingFrequency || '',
    reportingQuarter: mode === DUPLICATE_MODES.DEFAULTS_ONLY ? '' : src.reportingQuarter || '',
    reportingYear: mode === DUPLICATE_MODES.DEFAULTS_ONLY ? '' : src.reportingYear || new Date().getFullYear(),
    tenantId: user.tenantId,
    tenantName: user.tenantName || '',
    createdBy: user.email,
    createdAt: serverTimestamp(),
    updatedBy: user.email,
    updatedAt: serverTimestamp(),
    status: 'draft',
    duplicatedFrom: sourceAuditId,
    // When duplicating with defaults, mark the parent so the UI can
    // auto-unlock subsequent tabs and adjust onboarding behaviour.
    unlockedByDefaults: mode === DUPLICATE_MODES.DEFAULTS_ONLY,
  });

  // 4b. Subcollection docs — one per included form
  formsToCopy.forEach((key) => {
    let data = { ...sourceDataByForm[key] };

    if (mode === DUPLICATE_MODES.DEFAULTS_ONLY) {
      if (key === 'a') {
        data = sanitizeFormAForDefaults(sourceDataByForm['a']);
      } else if (key === 'e') {
        data = sanitizeFormEForDefaults(sourceDataByForm['a'], sourceDataByForm['e']);
      } else {
        data = {};
      }
    }

    // For COMPLETE and INSTALLATION_ONLY modes, just copy the source data.
    if (mode !== DUPLICATE_MODES.DEFAULTS_ONLY && Object.keys(data).length === 0) {
      const snap = sourceDataByForm[key];
      if (!snap) return;
      data = { ...snap };
    }

    data.updatedAt = new Date();
    data.meta = {
      ...(data.meta || {}),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    // When duplicating with defaults, mark each copied form as "started"
    // so the UI shows them unlocked and navigable without requiring the
    // user to click through every tab.
    if (mode === DUPLICATE_MODES.DEFAULTS_ONLY) {
      data.progressMeta = {
        progress: 10,
        status: 'In Progress',
        sections: {},
      };
    }

    batch.set(
      doc(db, 'auditReports', newAuditId, `form_${key}`, 'main'),
      data
    );
  });

  // 5. Commit atomically
  await batch.commit();

  return { newAuditId, newName: finalName };
}